import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  fetchStudentLiveLessonQuizState,
  joinLiveLessonQuiz,
  submitStudentLiveLessonQuizAnswer
} from '../../lib/liveLessonApi';
import type { StudentLiveLessonQuizState } from '../../lib/liveLessonTypes';

export default function StudentLiveQuiz() {
  const params = useParams<{ joinToken: string }>();
  const joinToken = String(params.joinToken || '');
  const [state, setState] = useState<StudentLiveLessonQuizState | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!joinToken) return;
    let mounted = true;
    let intervalId: number | undefined;

    const sync = async () => {
      try {
        const next = await fetchStudentLiveLessonQuizState(joinToken);
        if (mounted) setState(next);
      } catch (error: any) {
        if (!mounted) return;
        const message = error?.response?.data?.error || 'Quiz-Status konnte nicht geladen werden';
        toast.error(message);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const join = async () => {
      try {
        await joinLiveLessonQuiz(joinToken);
      } catch (error: any) {
        if (!mounted) return;
        toast.error(error?.response?.data?.error || 'Quiz-Beitritt fehlgeschlagen');
      }
      await sync();
    };

    void join();
    intervalId = window.setInterval(() => {
      void sync();
    }, 1500);

    return () => {
      mounted = false;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [joinToken]);

  const optionLabels = useMemo(() => ['A', 'B', 'C', 'D'], []);

  const handleAnswer = async (selectedIndex: number) => {
    if (!joinToken || !state?.question || state.hasAnsweredCurrentQuestion) return;
    setSubmitting(true);
    try {
      await submitStudentLiveLessonQuizAnswer(joinToken, { selectedIndex });
      const next = await fetchStudentLiveLessonQuizState(joinToken);
      setState(next);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Antwort konnte nicht gesendet werden');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!state) {
    return <div className="p-6 text-sm text-gray-600">Quiz nicht gefunden.</div>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-xs uppercase tracking-wide text-gray-500">Lernerfolgskontrolle</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">{state.session.topic}</h1>
      </div>

      {state.participantStatus === 'pending' ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800">
          Teilnahme angefragt. Warte auf Freigabe durch die Lehrkraft.
        </div>
      ) : null}

      {state.question ? (
        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">
            Frage {state.question.questionIndex + 1}/{state.question.totalQuestions}
          </p>
          <p className="text-lg font-semibold text-gray-900">{state.question.prompt}</p>
          <div className="grid gap-2">
            {state.question.options.map((option, idx) => (
              <button
                key={`${option}-${idx}`}
                type="button"
                disabled={submitting || state.hasAnsweredCurrentQuestion || state.participantStatus !== 'joined'}
                onClick={() => void handleAnswer(idx)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="mr-2 font-semibold">{optionLabels[idx]}.</span>
                {option}
              </button>
            ))}
          </div>
          {state.hasAnsweredCurrentQuestion ? (
            <p className="text-xs text-emerald-700">Antwort gespeichert.</p>
          ) : null}
        </div>
      ) : null}

      {state.reveal ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">
            Frage {state.reveal.questionIndex + 1}/{state.reveal.totalQuestions}
          </p>
          <p className={`mt-2 text-lg font-semibold ${state.reveal.wasCorrect ? 'text-emerald-700' : 'text-rose-700'}`}>
            {state.reveal.wasCorrect ? 'Richtig!' : 'Leider falsch.'}
          </p>
          <p className="mt-1 text-sm text-gray-700">Richtige Antwort: {state.reveal.correctOptionLabel || '—'}</p>
        </div>
      ) : null}

      {state.finished ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Highscore</h2>
          {state.finished.selfRank ? (
            <p className="mt-1 text-sm text-gray-600">Dein Platz: {state.finished.selfRank}</p>
          ) : null}
          <div className="mt-3 space-y-1">
            {state.finished.leaderboard.map((entry) => (
              <div
                key={`${entry.rank}-${entry.name}`}
                className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${
                  entry.isSelf ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'
                }`}
              >
                <span>{entry.rank}. {entry.name}</span>
                <span className="font-semibold">{entry.score}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
